import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

/**
 * Shared site-nav primitives — the `thea` wordmark and the right-grouped
 * text-link nav (People / Calendar / Gift Guides).
 *
 * Extracted from SiteHeader (2026-07-11, header/IA unification PR 1) so the
 * board header can render the SAME wordmark + nav links on desktop instead
 * of duplicating them. SiteHeader remains the reference layout; BoardHeader
 * composes these into its compact top row.
 *
 * Deliberately free of any auth/Firebase imports — BoardHeader pulls this
 * file without dragging HeaderAccountMenu's module graph along.
 */

export interface SiteHeaderNavItem {
  label: string;
  to: string;
  isActive: boolean;
}

export const SiteWordmark = styled.a<{ $overHero?: boolean }>`
  font-family: ${({ theme }) => theme.font.serif};
  font-style: italic;
  letter-spacing: 0.025em;
  font-size: 28px;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 200ms ease;
  ${({ $overHero }) =>
    $overHero
      ? css`
          color: #ffffff;
          text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
        `
      : css`
          color: hsl(var(--primary));
        `}
  &:hover {
    opacity: 0.8;
  }
`;

const Nav = styled.nav`
  display: none;
  /* Hidden at <1024px so mobile uses the bottom tab bar instead. */
  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
    gap: 24px;
  }
`;

/* THE-1185 — one of the board page's standard "tabs"-role nav rows (shared
   with BoardChipTabs): theme.text.tabs / theme.weight for size+weight, and
   the one "selected" recipe (semibold + foreground, Kate's approved
   #38322E ink + a scaleX underline vs. regular + mutedForeground + no
   underline) for active state, same as the chip strip. Desktop-only (see
   Nav above), so there's no narrower mobile size in play here. */
const NavLink = styled(Link)<{ $active: boolean; $overHero?: boolean }>`
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.text.tabs.desktop};
  text-decoration: none;
  letter-spacing: 0.005em;
  position: relative;
  padding-bottom: 4px;
  transition: color 150ms ease;
  font-weight: ${({ $active, theme }) =>
    $active ? theme.weight.semibold : theme.weight.regular};
  color: ${({ $overHero, $active, theme }) =>
    $overHero
      ? '#ffffff'
      : $active
        ? theme.color.foreground
        : theme.color.mutedForeground};
  text-shadow: ${({ $overHero }) =>
    $overHero ? '0 1px 6px rgba(0, 0, 0, 0.45)' : 'none'};
  /* THE-1185 — ink, not the brand accent: "ink 600 + a 2px ink underline,
     for the top nav and the category tabs alike" (Kate 2026-10-24), same
     as BoardChipTabs. $overHero keeps white, matching its white text. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: ${({ $overHero, theme }) =>
      $overHero ? '#ffffff' : theme.color.foreground};
    border-radius: 1px;
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: left center;
    transition: transform 180ms ease;
  }
  &:hover {
    /* Ink, not clay — the tab hover's own color (BoardChipTabs), so hover
       reads the same "about to select" cue everywhere on the board page
       instead of flashing the brand accent (QA round 4, #889).
       QA round 5, #889: over the hero photo the resting color is white, not
       ink ($overHero above) — the old unconditional foreground here made
       hovering a hero-photo nav link go dark mid-hover with nothing but the
       underline (which has its own $overHero branch below) explaining why.
       $overHero now keeps hover white too; the underline is the hover cue
       there, not a color change. */
    color: ${({ $overHero, theme }) => ($overHero ? '#ffffff' : theme.color.foreground)};
  }
  &:hover::after {
    /* Grow the full-width bar in from the left on hover (transform-origin is
       left center). Was scaleX(0.4) — a deliberate 40% stub that (a) underlined
       only part of the label and (b) made the active tab RETRACT to 40% when
       hovered. scaleX(1) = a conventional full-width hover underline. */
    transform: scaleX(1);
  }
`;

export interface SiteNavLinksProps {
  items: SiteHeaderNavItem[];
  overHero?: boolean;
}

/** The right-grouped text-link nav. Renders nothing when `items` is empty
 *  (useDesktopNav gates the whole nav on having ≥1 recipient). Hidden at
 *  <1024px — mobile uses the bottom tab bar. */
export const SiteNavLinks: React.FC<SiteNavLinksProps> = ({
  items,
  overHero = false,
}) => {
  if (items.length === 0) return null;
  return (
    <Nav aria-label="Primary">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          $active={item.isActive}
          $overHero={overHero}
          aria-current={item.isActive ? 'page' : undefined}
        >
          {item.label}
        </NavLink>
      ))}
    </Nav>
  );
};
